import {DeleteFilled, PlusOutlined, ReloadOutlined} from "@ant-design/icons";
import {Breadcrumb, Button, Card, Checkbox, Col, Form, Input, message, Modal, Row, Space, Table} from "antd";
import Column from "antd/es/table/Column";
import {useForm} from "antd/lib/form/Form";
import React, {useContext, useEffect, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {AppContextContext, TicketCreationDefinitionServiceContext} from "../../Contexts";
import Ticket from "../../domain/Ticket";
import TicketCreationDefinition from "../../domain/TicketCreationDefinition";
import {FilterBuilder} from "../../sal-ui/filter/FilterBuilder";
import {useTableHandler} from "../../sal-ui/TableHandler";
import PagedResult from "../../service/PagedResult";
import {DocumentTitle} from "../DocumentTitle";
import * as globalStyles from "../App.module.css";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";

import * as styles from "../ticketcreationdefinition/TicketCreationDefinitionList.module.css";
import {renderOrganizationLink} from "../common/OrganizationLink";

function TicketCreationDefinitionList() {
    const appContext = useContext(AppContextContext);
    const ticketCreationDefinitionService = useContext(TicketCreationDefinitionServiceContext);
    const navigate = useNavigate();
    const tableHandler = useTableHandler("id", {reloadFunction: reload});
    const [filterForm] = useForm();

    const [ticketCreationDefinitions, setTicketCreationDefinitions] = useState<PagedResult<TicketCreationDefinition>>();

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        reload();
    }, [])

    return (
        <DocumentTitle title={"Tickets"}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}>
                    <Breadcrumb.Item>{appContext.config?.appName}</Breadcrumb.Item>
                    <Breadcrumb.Item>Ticket rules</Breadcrumb.Item>
                </Breadcrumb>

                <h1>Ticket rules</h1>

                {false && renderFilterForm()}

                <div className={globalStyles["common__top-button-bar"]}>
                    <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={tableHandler.reload}/>
                    <Button type={"primary"}
                            icon={<PlusOutlined/>}
                            onClick={() => navigate("/definitions/ticket-rules/add")}>Add</Button>
                </div>

                <Table className={styles.table}
                       showSorterTooltip={false}
                       loading={tableHandler.loading}
                       dataSource={ticketCreationDefinitions?.data}
                       size="middle"
                       onChange={tableHandler.onTableChange}
                       pagination={tableHandler.pagination}
                       onRow={onRow}
                       rowKey="id">

                    <Column dataIndex="id" title={"ID"} width={80} align={"center"} render={renderId}/>
                    <Column
                        dataIndex="enabled"
                        title={"Enabled"}
                        sorter={true}
                        sortDirections={ascendingFirstSortDirections}
                        width={80}
                        align={"center"}
                        defaultSortOrder={"ascend"}
                        render={value => <Checkbox checked={value} disabled={true}/>}
                    />
                    <Column dataIndex="name" title={"Name"} sorter={true} sortDirections={ascendingFirstSortDirections} render={renderName} />
                    <Column
                        dataIndex={["organization", "name"]}
                        sorter={true}
                        sortDirections={ascendingFirstSortDirections}
                        title={"Organization"}
                        align={"left"}
                        render={renderOrganization}
                    />

                    <Column title={"Actions"} width='100px' render={renderAction}
                            className={globalStyles["table-actions"]}/>
                </Table>
            </>
        </DocumentTitle>
    )

    function renderId(value: string) {
        return (
            <span title={value}>{value.substring(0, 2) + '...' + value.substring(value.length - 2)}</span>
        )
    }

    function renderName(value: string, definition: TicketCreationDefinition) {
        return <Link to={`/definitions/ticket-rules/${definition.id}`}>{definition.name}</Link>;
    }

    function renderOrganization(value: string | undefined, definition: TicketCreationDefinition) {
        return renderOrganizationLink(definition.organization, value);
    }

    function renderFilterForm() {
        return (
            <Card className={`${globalStyles["common__filter"]} ${styles.filter}`}>
                <Form form={filterForm} layout={"vertical"} onFinish={onFinishFilter} labelAlign={"left"}>
                    <Row gutter={24}>
                        <Col span={8}>
                            <Form.Item
                                name={"quickSearch"}
                                label={"Filter"}>
                                <Input/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col span={4}>
                            <Form.Item>
                                <Space>
                                    <Button type={"primary"}
                                            htmlType={"submit"}>{"Apply"}</Button>
                                    <Button htmlType={"reset"}
                                            onClick={onResetFilter}>{"Cancel"}</Button>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        )
    }

    function onFinishFilter(values: any) {
        const builder = new FilterBuilder();

        values.quickSearch && builder.quickSearch(values.quickSearch);

        tableHandler.onSearchSubmit(builder.build());
    }

    function onResetFilter() {
        tableHandler.onSearchSubmit('');

        filterForm.resetFields();
    }

    function renderTitle(value: any, ticket: Ticket, index: number) {
        return <Link to={`/ticket-creation-definitions/${ticket.id}`}>{value}</Link>;
    }


    function renderAction(value: any, ticket: Ticket, index: number) {
        return (
            <>
                <Button icon={<DeleteFilled/>} className={"ant-btn-icon-only"}
                        title={"Delete"}
                        onClick={() => {
                            Modal.confirm({
                                content: "Do you really want to delete this item?",
                                okText: "Delete",
                                cancelText: "Cancel",
                                onOk: () => onDeleteConfirm(ticket)
                            });
                        }}/>
            </>
        )
    }

    function onDeleteConfirm(ticket: Ticket) {
        ticketCreationDefinitionService.delete(ticket)
            .then(() => {
                message.success("Item successfully deleted.");

                reload();
            });
    }

    function reload() {
        return ticketCreationDefinitionService.getList(tableHandler.queryOptions).then(value => {
            tableHandler.updateTotal(value.total);

            setTicketCreationDefinitions(value);
        });
    }

    function onRow(ticket: Ticket, index?: number) {
        return {onDoubleClick: () => navigate(`/ticket-creation-definitions/${ticket.id}`)}
    }
}

export default TicketCreationDefinitionList;
